import {Injectable} from '@angular/core';
import {Action, Store} from '@ngrx/store';
import {Actions, Effect, ofType} from '@ngrx/effects';

import {forkJoin, Observable, of} from 'rxjs';

import {EventService, IDeviceMinAvg} from '../../../dashboard/shared';
import * as airData from '../actions/air-data.action';
import * as fromRoot from '../../../reducers';
import {Device} from '../../../afb-api/models/device';
import {catchError, concatMap, map, switchMap, withLatestFrom} from 'rxjs/internal/operators';

@Injectable()
export class AirDataEffects {

  @Effect()
  updateAirData$: Observable<Action> = this.actions$.pipe(
    ofType(airData.UPDATE_AIR_DATA),
    withLatestFrom(this.state$),
    map(([action, state]: [airData.UpdateAirDataAction, fromRoot.State]) => {
      const payload = <{
        orgToken: string,
        selectedDeviceList: Device[],
        interval: number,
        fromDate: Date,
        toDate: Date,
        desc?: boolean,
        limit?: number
      }>action.payload;
      const deviceUuidList: string[] = payload.selectedDeviceList.map(
        (selectedDevice: Device) => `${selectedDevice.deviceType}_${selectedDevice.deviceId}`
      );

      if (deviceUuidList.length === 0) {
        return [
          new airData.StartLoadingAction(),
          new airData.ClearAirDataAction()
        ];
      } else {
        return [
          new airData.StartLoadingAction(),
          new airData.RequestAirDataAction({
            orgToken: payload.orgToken,
            selectedDeviceUuidList: deviceUuidList,
            interval: payload.interval,
            fromDate: payload.fromDate,
            toDate: payload.toDate
          })
        ];
      }
    }),
    concatMap((actionList: any[]) => actionList),
    catchError(() => of(new airData.UpdateDeviceListFailAction()))
  );

  @Effect()
  updateDeviceList$: Observable<Action> = this.actions$.pipe(
    ofType(airData.UPDATE_DEVICE_LIST),
    withLatestFrom(this.state$),
    map(([action, state]: [airData.UpdateDeviceListAction, fromRoot.State]) => {
      const payload = <{ orgToken: string, selectedDeviceList: Device[] }>action.payload;
      const deviceUuidList: string[] = payload.selectedDeviceList.map(
        (selectedDevice: Device) => `${selectedDevice.deviceType}_${selectedDevice.deviceId}`
      );

      if (deviceUuidList.length === 0) {
        return [
          new airData.StartLoadingAction(),
          new airData.ClearAirDataAction()
        ];
      } else {
        return [
          new airData.StartLoadingAction(),
          new airData.RequestAirDataAction({
            orgToken: payload.orgToken,
            selectedDeviceUuidList: deviceUuidList,
            interval: state.airData.interval,
            fromDate: state.airData.fromDate,
            toDate: state.airData.toDate
          })
        ];
      }
    }),
    concatMap((actionList: any[]) => actionList),
    catchError(() => of(new airData.UpdateDeviceListFailAction()))
  );

  @Effect()
  updateTimeRange$: Observable<Action> = this.actions$.pipe(
    ofType(airData.UPDATE_TIME_RANGE),
    withLatestFrom(this.state$),
    map(([action, state]: [airData.UpdateTimeRangeAction, fromRoot.State]) => {
      const payload = <{ orgToken: string, fromDate: Date, toDate: Date }>action.payload;

      if (state.airData.selectedDeviceUuidList.length === 0) {
        return [
          new airData.StartLoadingAction(),
          new airData.ClearAirDataAction()
        ];
      } else {
        return [
          new airData.StartLoadingAction(),
          new airData.RequestAirDataAction({
            orgToken: payload.orgToken,
            selectedDeviceUuidList: state.airData.selectedDeviceUuidList,
            interval: state.airData.interval,
            fromDate: payload.fromDate,
            toDate: payload.toDate
          })
        ];
      }
    }),
    concatMap((actionList: any[]) => actionList),
    catchError(() => of(new airData.UpdateTimeRangeFailAction()))
  );

  @Effect()
  requestAirData$: Observable<Action> = this.actions$.pipe(
    ofType(airData.REQUEST_AIR_DATA),
    withLatestFrom(this.state$),
    switchMap(([action, state]: [airData.RequestAirDataAction, fromRoot.State]) => {
      const payload = <{
        orgToken: string,
        selectedDeviceUuidList: string[],
        interval: number,
        fromDate: Date,
        toDate: Date,
        desc?: boolean,
        limit?: number
      }>action.payload;

      const selectedDeviceUuidList = [...payload.selectedDeviceUuidList];

      const obs: Observable<IDeviceMinAvg[]>[] = [];
      for (let i = 0; i < selectedDeviceUuidList.length; i++) {
        const arrUuid = selectedDeviceUuidList[i].split('_');  // [0]: Type, [1]: ID
        obs.push(this.eventService.requestDeviceMinAvg(payload.orgToken, Number(arrUuid[1]),
          arrUuid[0], payload.interval, payload.fromDate, payload.toDate));
      }

      return forkJoin(obs).pipe(
        map((deviceMinAvgList: Array<IDeviceMinAvg[]>) => {
          return {
            'deviceMinAvgList': deviceMinAvgList,
            'timeRange': {fromDate: payload.fromDate, toDate: payload.toDate},
            'selectedDeviceUuidList': selectedDeviceUuidList
          };
        })
      );
    }),
    map((data: {
      deviceMinAvgList: Array<IDeviceMinAvg[]>,
      timeRange: { fromDate: Date, toDate: Date },
      selectedDeviceUuidList: string[]
    }) => {
      // Create air data entities
      const newEntities = data.deviceMinAvgList.reduce(
        (entities: { [deviceUuid: string]: IDeviceMinAvg[] }, deviceMinAvg: IDeviceMinAvg[], index: number) => {
          return Object.assign(entities, {
            [data.selectedDeviceUuidList[index]]: deviceMinAvg
          });
        }, {});
      return new airData.RequestSuccessAction({entities: newEntities, timeRange: data.timeRange});
    }),
    catchError(() => of(new airData.RequestFailAction()))
  );

  constructor(private actions$: Actions,
              private state$: Store<fromRoot.State>,
              private eventService: EventService) {
  }
}
